import * as React from "react";
import { CheckIcon, ChevronsUpDown } from "lucide-react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import useCloseOnWindowBlur from "@/hooks/useCloseOnWindowBlur";
import { getOrderedCountryCodes, DEFAULT_COUNTRY, sortCountryOptions, PRIORITY_COUNTRIES } from "@/lib/priorityCountries";

/**
 * PhoneInput — country dialling-code selector + local-number input (Issue #440).
 *
 * Adapted from omeralpi/shadcn-phone-input (MIT). Built on react-phone-number-input
 * (which wraps libphonenumber-js). Uses shadcn primitives we already have.
 *
 * Props:
 *   value       — E.164 string (e.g. "+19173028542") or undefined
 *   onChange    — receives E.164 string or undefined
 *   defaultCountry — ISO alpha-2 code (default: "US" — issue #440 decision)
 *   placeholder — input placeholder text
 *   className   — additional className for the outer wrapper
 *
 * The priority block (US, AU, NZ, GB, CA) is rendered first in the dropdown,
 * separated by a CommandSeparator from the remainder (alphabetical by ISO code).
 * See priorityCountries.js.
 */
const PhoneInput = React.forwardRef(
  ({ className, onChange, value, ...props }, ref) => {
    return (
      <RPNInput.default
        ref={ref}
        className="flex w-full"
        flagComponent={FlagComponent}
        countrySelectComponent={CountrySelect}
        inputComponent={InputComponent}
        smartCaret={false}
        value={value || undefined}
        onChange={(value) => onChange?.(value || "")}
        inputProps={{ className }}
        {...props}
      />
    );
  },
);
PhoneInput.displayName = "PhoneInput";

const InputComponent = React.forwardRef(
  ({ className, ...props }, ref) => (
    <Input
      className={cn("h-auto px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors border-stone-200 focus:ring-stone-400 rounded-e-lg rounded-s-none", className)}
      {...props}
      ref={ref}
    />
  ),
);
InputComponent.displayName = "InputComponent";

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}) => {
  const [searchValue, setSearchValue] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  const listRef = React.useRef(null);
  // Embedded forms: a host-page click never reaches this iframe's document,
  // so Radix can't dismiss the picker — close it when the window loses focus
  // (#850). No onBlur here: the number input already blurred when it opened.
  useCloseOnWindowBlur(isOpen, () => setIsOpen(false));
  const [activeCountry, setActiveCountry] = React.useState(selectedCountry);

  const orderedCountryList = React.useMemo(
    () => sortCountryOptions(countryList),
    [countryList],
  );


  React.useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      const list = listRef.current;
      if (!list) return;
      const selected = list.querySelector('[data-selected="true"]');
      if (!selected) return;
      const listRect = list.getBoundingClientRect();
      const itemRect = selected.getBoundingClientRect();
      list.scrollTop += itemRect.top - listRect.top;
    });
  }, [isOpen]);

  const priorityCountries = React.useMemo(
    () =>
      orderedCountryList.filter((c) =>
        PRIORITY_COUNTRIES.includes(c.value),
      ),
    [orderedCountryList],
  );
  const restCountries = React.useMemo(
    () =>
      orderedCountryList.filter(
        (c) => !PRIORITY_COUNTRIES.includes(c.value),
      ),
    [orderedCountryList],
  );

  return (
    <Popover
      open={isOpen}
      modal
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) {
          setSearchValue("");
          setActiveCountry(selectedCountry);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="flex h-auto gap-1 rounded-e-none rounded-s-lg border-r-0 px-3 focus:z-10"
          disabled={disabled}
        >
          <FlagComponent
            country={selectedCountry}
            countryName={selectedCountry}
          />
          <ChevronsUpDown
            className={cn(
              "-mr-2 size-4 opacity-50",
              disabled ? "hidden" : "opacity-100",
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command value={activeCountry} onValueChange={setActiveCountry} className="max-h-60">
          <CommandInput
            value={searchValue}
            onValueChange={(v) => {
              setSearchValue(v);

              if (!v && listRef.current) {
                requestAnimationFrame(() => {
                  if (listRef.current) listRef.current.scrollTop = 0;
                });
              }
            }}
            placeholder="Search country..."
          />
          <CommandList ref={listRef}>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {priorityCountries.map(({ value, label }) =>
                value ? (
                  <CountrySelectOption
                    key={value}
                    country={value}
                    countryName={label}
                    selectedCountry={selectedCountry}
                    onChange={onChange}
                    onSelectComplete={() => setIsOpen(false)}
                  />
                ) : null,
              )}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              {restCountries.map(({ value, label }) =>
                value ? (
                  <CountrySelectOption
                    key={value}
                    country={value}
                    countryName={label}
                    selectedCountry={selectedCountry}
                    onChange={onChange}
                    onSelectComplete={() => setIsOpen(false)}
                  />
                ) : null,
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}) => {
  const handleSelect = () => {
    onChange(country);
    onSelectComplete();
  };

  return (
    <CommandItem
      className="gap-2"
      value={country}
      keywords={[countryName]}
      onSelect={handleSelect}
    >
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-sm text-foreground/50">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
      <CheckIcon
        className={`ml-auto size-4 ${country === selectedCountry ? "opacity-100" : "opacity-0"}`}
      />
    </CommandItem>
  );
};

const FlagComponent = ({ country, countryName }) => {
  const Flag = flags[country];

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/20 [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

export function NurturePhoneInput({ value, ...props }) {
  const valueProp = value ? { value } : {};
  return (
    <PhoneInput
      countries={getOrderedCountryCodes()}
      defaultCountry={DEFAULT_COUNTRY}
      international
      {...valueProp}
      {...props}
    />
  );
}

export { PhoneInput };
